import type { SpecsConfig } from "@/components/settings/forms/resources-form/SpecsConfigTypes";
import type { Environment } from "@/gen_src/proto/chalk/server/v1/environment_pb";
import { getSpecsConfigJsonFromEnvironment } from "@/utils/teamService";

export interface EngineImageOverride {
  displayKey: string;
  imageUrl: string;
  source: "environment" | "service";
  resourceGroup?: string;
  serviceKind?: string;
}

export interface EngineImageOverrideResult {
  hasOverride: boolean;
  overrides: EngineImageOverride[];
}

export function getEngineImageOverrides({
  specsConfig,
  pinnedBaseImage,
}: {
  specsConfig?: SpecsConfig;
  pinnedBaseImage?: string;
}): EngineImageOverride[] {
  const overrides: EngineImageOverride[] = [];

  if (pinnedBaseImage) {
    overrides.push({
      displayKey: "base environment sql",
      imageUrl: pinnedBaseImage,
      source: "environment",
    });
  }

  if (specsConfig?.services) {
    for (const [serviceKind, service] of Object.entries(specsConfig.services)) {
      if (!service.engine_image_uri_override) {
        continue;
      }

      overrides.push({
        displayKey: `default > ${serviceKind}`,
        imageUrl: service.engine_image_uri_override,
        resourceGroup: "default",
        serviceKind,
        source: "service",
      });
    }
  }

  if (specsConfig?.resource_groups) {
    for (const group of specsConfig.resource_groups) {
      if (!group.services) {
        continue;
      }

      const resourceGroup = group.resource_group_name || "unnamed";
      for (const [serviceKind, service] of Object.entries(group.services)) {
        if (!service.engine_image_uri_override) {
          continue;
        }

        overrides.push({
          displayKey: `${resourceGroup} > ${serviceKind}`,
          imageUrl: service.engine_image_uri_override,
          resourceGroup,
          serviceKind,
          source: "service",
        });
      }
    }
  }

  return overrides;
}

export function getEngineImageOverridesFromEnvironment(
  environment: Environment,
): EngineImageOverride[] {
  return getEngineImageOverrides({
    pinnedBaseImage: environment.pinnedBaseImage,
    specsConfig: getSpecsConfigJsonFromEnvironment(environment),
  });
}
